import {
  Box,
  Container,
  Switch,
  makeStyles,
} from "@material-ui/core";
import {
  InfoBox,
  InfoLine,
} from "../components-molecules";
import { SubscriptionDetailHeader } from "../components-molecules/SubscriptionDetailHeader";
import { ITheme, colours } from "../constants/theme";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { getProSubscription, ProTeamSubscription, proTogglePremium, proTogglePremiumOnly, setProTeamPayAsYouGo, setProTeamPremiumFee } from "../redux/actions/subscriptions";
import { useParams } from "react-router-dom";
import moment from "moment";
import { textButtonHoverAndActive } from "../utils/styles/buttonStyles";
import { SubscriptionPayAsYouGoModal } from "../components-molecules/SubscriptionPayAsYouGo";
import { SubscriptionPremiumFeeModal } from "../components-molecules/SubscriptionPremiumFee";
import { DataUpdateSocketMessage } from "./Subscriptions";
import { useAppWebSocket } from "../hooks/useAppWebSocket";
import { useTranslation } from 'react-i18next';
import { getShortDateFormat } from "../utils/dateTimeFormatting";

const useStyles = makeStyles((theme: ITheme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  detailsContainer: {
    marginTop: "24px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  stores: {
    marginTop: 16,
  },
  storeList: {
    // InfoLine offset
    marginLeft: -17.5,
    marginRight: 17.5,
    marginBottom: 12
  },
  storeContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 8,
  },
  storeInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  storeImage: {
    borderRadius: 4,
    border: `1px solid ${colours.dark12}`,
  },
  active: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  suspended: {
    color: theme.palette.warning.main,
    fontWeight: 700,
  },
  cancelButton: {
    padding: '12px 16px',
    borderRadius: '8px',
    color: theme.palette.primary.main,
    ...textButtonHoverAndActive
  }
}));

enum SubscriptionCardModal {
  ADD_DELIVERY_TRANCHE,
  SUSPEND,
  ACTIVATE,
  PAY_AS_YOU_GO,
  PREMIUM_FEE
}

export const SubscriptionDetailPro = () => {
  const { t } = useTranslation();
  const params: any = useParams();
  const userData = useSelector((state: any) => state.login);
  const isAdmin = useMemo(() => userData.role === "Admin", [userData]);
  const classes = useStyles();
  const query = new URLSearchParams(window.location.search);
  const isPro = Boolean(query.get('isPro'));

  const [visibleModal, setVisibleModal] = useState<SubscriptionCardModal>();
  const [subscription, setSubscription] = useState<ProTeamSubscription>();
  const [waitingForResponse, setWaitingForResponse] = useState(false);

  const [premiumEnabled, setPremiumEnabled] = useState(false);
  const [premiumOnly, setPremiumOnly] = useState(false);

  const [listLastUpdate, setListLastUpdate] = useState<string>();
  const { lastJsonMessage } = useAppWebSocket<DataUpdateSocketMessage | null>(
    '/ws/data-update',
    {}
  );

  useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.LastUpdate !== listLastUpdate) {
      setListLastUpdate(lastJsonMessage.LastUpdate);
      getProData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage, lastJsonMessage?.LastUpdate, lastJsonMessage?.UpdateType]);

  function getProData() {
    getProSubscription(
      params.id,
      (res) => {
        setSubscription(res);
        setPremiumEnabled(res.PremiumEnabled);
        setPremiumOnly(res.PremiumOnly);
      },
      (err) => { /* Removed console.log */ }
    );
  }

  const setPayAsYouGo = (payg: number) => {
    if (subscription) {
      setProTeamPayAsYouGo(
        { ProTeamId: subscription.ProTeamId, Price: Math.round(payg * 100) },
        (res) => {
          getProData();
          setWaitingForResponse(false);
          setVisibleModal(undefined);
        },
        (err) => { /* Removed console.log */ }
      );
    }
  };

  const setPremiumFee = (pf: number) => {
    if (subscription) {
      setProTeamPremiumFee(
        { ProTeamId: subscription.ProTeamId, Fee: Math.round(pf * 100) },
        (res) => {
          getProData();
          setWaitingForResponse(false);
          setVisibleModal(undefined);
        },
        (err) => { /* Removed console.log */ }
      );
    }
  };

  const togglePremium = () => {
    if (!subscription?.ProTeamId) return null;
    proTogglePremium(subscription?.ProTeamId, () => {
      getProData();
    }, () => { });
  }

  const togglePremiumOnly = () => {
    if (!subscription?.ProTeamId) return null;
    proTogglePremiumOnly(subscription?.ProTeamId, () => {
      getProData();
    }, () => { });
  }


  useEffect(() => {
    getProData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  return (
    <Box className={classes.container}>
      <Container className={classes.root}>
        {(isAdmin && !isPro) &&
          <SubscriptionDetailHeader
            name={subscription?.ProTeamName}
            type={"Pro"}
            status={subscription ? subscription?.HasActiveSubscription ? "Active" : "Inactive" : undefined}
            ProTeamId={subscription?.ProTeamId}
            totalCreditsRemaining={subscription?.TotalCreditsRemaining}
          />
        }
        <Container className={classes.detailsContainer}>
          <InfoBox title="Subscription Information">
            <InfoLine
              clickable
              name="Available credits"
              value={subscription?.TotalCreditsRemaining ? subscription.TotalCreditsRemaining : "-"}
            >
            </InfoLine>
            <InfoLine
              clickable
              name="Start date"
              value={subscription?.SubscriptionStartDate ? moment(subscription.SubscriptionStartDate).format(getShortDateFormat()) : "-"} />
            <InfoLine
              clickable
              name="End date"
              value={subscription?.SubscriptionStartDate ? moment(subscription.SubscriptionStartDate).add(1, "month").format(getShortDateFormat()) : "-"} />
            <InfoLine
              clickable
              name="Pay as you go price"
value={subscription?.PayAsYouGoPrice ? `${t('currencySymbol')}${(subscription.PayAsYouGoPrice / 100).toFixed(2)}` : '-'}
              onClick={() => setVisibleModal(SubscriptionCardModal.PAY_AS_YOU_GO)}>
              <InfoLine.ArrowArea></InfoLine.ArrowArea>
            </InfoLine>
            <InfoLine
              clickable
              name="Concierge fee"
value={subscription?.PremiumFee ? `${t('currencySymbol')}${(subscription.PremiumFee / 100).toFixed(2)}` : '-'}
              onClick={() => setVisibleModal(SubscriptionCardModal.PREMIUM_FEE)}>
              <InfoLine.ArrowArea></InfoLine.ArrowArea>
            </InfoLine>
            <InfoLine
              clickable
              name="Concierge Enabled"
              value={() =>
                <Switch
                  disabled={waitingForResponse}
                  checked={premiumEnabled}
                  color="primary"
                  onChange={togglePremium}
                  name="checkedA"
                  inputProps={{ 'aria-label': 'secondary checkbox' }}
                />
              }>
            </InfoLine>
            <InfoLine
              clickable
              name="Don't show individual quotes"
              value={() =>
                <Switch
                  disabled={waitingForResponse}
                  checked={premiumOnly}
                  color="primary"
                  onChange={togglePremiumOnly}
                  name="checkedA"
                  inputProps={{ 'aria-label': 'secondary checkbox' }}
                />
              }>
            </InfoLine>
          </InfoBox>
        </Container>
      </Container>

      <SubscriptionPayAsYouGoModal
        classes={classes}
        isVisible={visibleModal === SubscriptionCardModal.PAY_AS_YOU_GO}
        isDisabled={waitingForResponse}
        close={() => setVisibleModal(undefined)}
        setPayAsYouGo={setPayAsYouGo}
        defaultValue={subscription?.PayAsYouGoPrice ? (subscription?.PayAsYouGoPrice / 100).toFixed(2) : null}
      />
      <SubscriptionPremiumFeeModal
        classes={classes}
        isVisible={visibleModal === SubscriptionCardModal.PREMIUM_FEE}
        isDisabled={waitingForResponse}
        close={() => setVisibleModal(undefined)}
        setPremiumFee={setPremiumFee}
        defaultValue={subscription?.PremiumFee ? (subscription?.PremiumFee / 100).toFixed(2) : null}
      />
    </Box>
  );
};
